import { nextTick } from "vue";
import { get_token } from "@/router";
import { export_personal_profile_form_table_csv } from "@/utils/csv_styling";
import portal_form_answers_helper from "./portal_form_answers_helper";

export default {
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    personal_profile_forms_users: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    portal_id() {
      return this.$route.params.portal_id;
    },
    form_tables() {
      const forms_map = new Map();

      (this.personal_profile_forms_users || []).forEach((profile_entry) => {
        (profile_entry.forms || []).forEach((form_entry) => {
          this.add_profile_form_table_entry(forms_map, profile_entry, form_entry);
        });
      });

      return Array.from(forms_map.values());
    },
    portal_form_tables() {
      const forms_map = new Map();

      this.add_portal_form_table_entries(forms_map);
      return Array.from(forms_map.values());
    },
  },
  watch: {
    loading() {
      this.sync_datatables();
    },
    form_tables() {
      this.sync_datatables();
    },
    portal_form_tables() {
      this.sync_datatables();
    },
    loading_portal_forms_answers() {
      this.sync_datatables();
    },
    '$route.params.portal_id': {
      handler(newPortalId) {
        this.expanded_portal_answer_table_id = null;
        this.get_portal_forms_answers(newPortalId);
      },
      immediate: true
    },
  },
  mounted() {
    this.sync_datatables();
  },
  beforeUnmount() {
    this.destroy_personal_profile_form_datatables();
  },
  data() {
    return ({
      portal_forms_answers: [],
      loading_portal_forms_answers: false,
      expanded_portal_answer_table_id: null,
    });
  },
  methods: {
    ...portal_form_answers_helper,
    getToken() {
      return get_token();
    },
    has_form_answers(form_entry) {
      return Boolean(form_entry?.answers?.length && form_entry?.form?.id);
    },
    get_form_table_key(form_id, table_type) {
      return `${table_type}-${form_id}`;
    },
    get_or_create_form_table(forms_map, form_entry, table_type = "profile") {
      const form_id = String(form_entry.form.id);
      const table_key = this.get_form_table_key(form_id, table_type);

      if (!forms_map.has(table_key)) {
        forms_map.set(table_key, {
          id: table_key,
          type: table_type,
          form: form_entry.form,
          questions: [],
          rows: [],
        });
      }

      return forms_map.get(table_key);
    },
    get_form_question_sections(form_entry) {
      const form_sections = form_entry?.form?.form_sections || [];
      return form_sections.flatMap((form_section) => {
        return (form_section?.section?.question_sections || []).map((question_section) => {
          return ({
            ...question_section,
            section: form_section?.section,
          });
        });
      });
    },
    get_question_section_id(question_section) {
      return String(question_section?.question?.id || "");
    },
    add_form_table_question_from_section(table, question_section) {
      const question_id = this.get_question_section_id(question_section);

      if (!question_id) {
        return;
      }

      this.add_form_table_question(table, question_section.question, question_id);
    },
    add_form_table_questions(table, form_entry) {
      for (const question_section of this.get_form_question_sections(form_entry)) {
        this.add_form_table_question_from_section(table, question_section);
      }
    },
    get_answer_question_id(answer) {
      return String(answer?.question?.id || answer?.question_id || answer?.id);
    },
    get_form_table_question_label(question_source) {
      return question_source?.question?.question || question_source?.question || "Question";
    },
    add_form_table_question(table, answer, question_id) {
      const has_question = table.questions.some((question) => String(question.id) === question_id);

      if (has_question) {
        return;
      }

      table.questions.push({
        id: question_id,
        question: this.get_form_table_question_label(answer),
      });
    },
    get_answer_file_names(answer) {
      return (answer.files || [])
        .map((file_item) => file_item?.file?.name)
        .filter(Boolean);
    },
    get_file_answer_text(cell_text, file_names) {
      const file_text = `Files: ${file_names.join(", ")}`;

      if (cell_text) {
        return `${cell_text} | ${file_text}`;
      }

      return file_text;
    },
    get_answer_cell_text(answer) {
      const file_names = this.get_answer_file_names(answer);
      const cell_text = answer?.answer || "";

      if (!file_names.length) {
        return cell_text || "-";
      }

      return this.get_file_answer_text(cell_text, file_names);
    },
    add_form_table_answer(table, answer_map, answer) {
      const question_id = this.get_answer_question_id(answer);

      this.add_form_table_question(table, answer, question_id);
      Reflect.set(answer_map, question_id, {
        text: this.get_answer_cell_text(answer),
        files: answer.files || [],
      });
    },
    build_form_answer_map(table, form_entry) {
      const answer_map = {};

      for (const answer of form_entry.answers) {
        this.add_form_table_answer(table, answer_map, answer);
      }

      return answer_map;
    },
    get_profile_detail_source(profile_entry) {
      return profile_entry?.base_profile || profile_entry?.profile || profile_entry || {};
    },
    get_profile_detail_value(profile_entry, field) {
      const profile_source = this.get_profile_detail_source(profile_entry);
      return Reflect.get(profile_source, field) || Reflect.get(profile_entry?.user || {}, field) || "-";
    },
    add_form_table_row(table, profile_entry, answer_map) {
      table.rows.push({
        user: profile_entry.user,
        base_profile_id: profile_entry.base_profile_id,
        location: this.get_profile_detail_value(profile_entry, "location"),
        date_of_birth: this.get_profile_detail_value(profile_entry, "date_of_birth"),
        race: this.get_profile_detail_value(profile_entry, "race"),
        gender: this.get_profile_detail_value(profile_entry, "gender"),
        answers: answer_map,
      });
    },
    add_profile_form_table_entry(forms_map, profile_entry, form_entry) {
      if (!this.has_form_answers(form_entry)) {
        return;
      }

      const table = this.get_or_create_form_table(forms_map, form_entry);
      const answer_map = this.build_form_answer_map(table, form_entry);
      this.add_form_table_row(table, profile_entry, answer_map);
    },
    sync_datatables() {
      nextTick(() => {
        this.destroy_personal_profile_form_datatables();
        if (!this.loading && !this.loading_portal_forms_answers && this.has_form_answer_tables()) {
          this.init_personal_profile_form_datatables();
        }
      });
    },
    has_form_answer_tables() {
      return this.form_tables.length > 0 || this.portal_form_tables.length > 0;
    },
    init_personal_profile_form_datatables() {
      if (!$.fn.DataTable) {
        return;
      }

      $(".personal-profile-form-table").each(function () {
        const table = $(this);

        if ($.fn.DataTable.isDataTable(this)) {
          table.DataTable().clear().destroy();
        }

        table.DataTable({
          paging: true,
          searching: true,
          ordering: true,
          info: true,
          lengthChange: true,
          pageLength: 10,
          lengthMenu: [10, 25, 50, 100],
          scrollX: true,
          autoWidth: false,
          layout: {
            topStart: "pageLength",
            topEnd: "search",
            bottomStart: "info",
            bottomEnd: "paging",
          },
          language: {
            paginate: {
              previous: "Previous",
              next: "Next",
            },
            info: "Showing _START_ to _END_ of _TOTAL_ entries",
            infoEmpty: "No entries available",
            search: "Search:",
          },
        });
      });
    },
    destroy_personal_profile_form_datatables() {
      if (!$.fn.DataTable) {
        return;
      }

      $(".personal-profile-form-table").each(function () {
        if ($.fn.DataTable.isDataTable(this)) {
          $(this).DataTable().destroy();
        }
      });
    },
    get_personal_profile_user_name(user) {
      return [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.email || "Unknown user";
    },
    get_personal_profile_answer_cell(row, question_id) {
      return row?.answers?.[String(question_id)] || { text: "-", files: [] };
    },
    export_personal_profile_form_table(form_table) {
      export_personal_profile_form_table_csv(form_table, {
        getUserName: this.get_personal_profile_user_name,
        getAnswerCell: this.get_personal_profile_answer_cell,
      });
    },
  },
};
